import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  FC,
  useId,
  type CSSProperties,
} from "react";

interface AgentStyleSliderProps {
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
  width?: number | "100%";
}

const DEFAULT_SLIDER_WIDTH = 160;

const ANCHOR_R = 11;
const SVG_H = 28;
const TRACK_Y = 11;
const TRACK_H = 6;
const THUMB_R = 10;
const THUMB_STROKE_W = 2;
const TRACK_BG = "#ECEFF0";
const TRACK_PROGRESS = "#1A2023";
const THUMB_GRADIENT_FROM = "#2196F3";
const THUMB_GRADIENT_TO = "#8A41D3";
const THUMB_STROKE = "#FFFFFF";
const THUMB_STYLE: CSSProperties = {
  filter: "drop-shadow(0 0 2px rgba(255,255,255,0.95))",
};

export const AgentStyleSlider: FC<AgentStyleSliderProps> = ({
  min,
  max,
  step,
  onChange,
  onInteractionStart,
  onInteractionEnd,
  value,
  width = DEFAULT_SLIDER_WIDTH,
}) => {
  const cy = SVG_H / 2;
  const trackRx = TRACK_H / 2;
  const gradientId = useId().replace(/:/g, "");

  const [dragging, setDragging] = useState(false);
  const [measuredWidth, setMeasuredWidth] = useState(DEFAULT_SLIDER_WIDTH);
  const svgRef = useRef<SVGSVGElement>(null);
  const circleRef = useRef<SVGCircleElement>(null);
  const progressRef = useRef<SVGRectElement>(null);

  const sliderWidth = width === "100%" ? measuredWidth : width;

  useLayoutEffect(() => {
    if (width !== "100%") {
      setMeasuredWidth(width);
      return undefined;
    }

    const updateMeasuredWidth = () => {
      const svgWidth = svgRef.current?.getBoundingClientRect().width;
      if (!svgWidth) return;

      setMeasuredWidth(Math.max(svgWidth - ANCHOR_R * 2, 0));
    };

    updateMeasuredWidth();

    if (!svgRef.current) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      updateMeasuredWidth();
    });
    resizeObserver.observe(svgRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [width]);

  useEffect(() => {
    if (!circleRef.current || !progressRef.current) return;
    const valueRange = max - min;
    if (valueRange <= 0) return;

    const clampedValue = Math.min(Math.max(value, min), max);
    const percents = (clampedValue - min) / valueRange;
    const percentage = Math.round(sliderWidth * percents);
    circleRef.current.setAttribute(
      "cx",
      (percents * sliderWidth + ANCHOR_R).toString()
    );
    progressRef.current.setAttribute("width", percentage.toString());
  }, [max, min, value, sliderWidth]);

  const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || !circleRef.current) return;
    if (sliderWidth <= 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    let clickPosition = e.clientX - rect.left - ANCHOR_R;
    if (clickPosition < 0) {
      clickPosition = 0;
    } else if (clickPosition > sliderWidth) {
      clickPosition = sliderWidth;
    }
    let newValue = min + (clickPosition / sliderWidth) * (max - min);
    newValue = Math.round(newValue / step) * step;

    if (newValue < min) {
      newValue = min;
    } else if (newValue > max) {
      newValue = max;
    }
    onChange(newValue);
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!dragging) return;
    handleClick(e);
  };

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    onInteractionStart?.();
    setDragging(true);
  };

  useEffect(() => {
    const handleDocumentMouseUp = () => {
      if (dragging) {
        onInteractionEnd?.();
        setDragging(false);
      }
    };

    document.addEventListener("mouseup", handleDocumentMouseUp);

    return () => {
      document.removeEventListener("mouseup", handleDocumentMouseUp);
    };
  }, [dragging, onInteractionEnd]);

  return (
    <svg
      ref={svgRef}
      className="cursor-pointer select-none"
      width={width === "100%" ? "100%" : width + ANCHOR_R * 2}
      height={SVG_H}
      viewBox={`0 0 ${sliderWidth + ANCHOR_R * 2} ${SVG_H}`}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor={THUMB_GRADIENT_FROM} />
          <stop offset="100%" stopColor={THUMB_GRADIENT_TO} />
        </linearGradient>
      </defs>
      <rect
        x={ANCHOR_R}
        y={TRACK_Y}
        width={sliderWidth}
        height={TRACK_H}
        fill={TRACK_BG}
        rx={trackRx}
      />
      <rect
        ref={progressRef}
        x={ANCHOR_R}
        y={TRACK_Y}
        width="0"
        height={TRACK_H}
        fill={TRACK_PROGRESS}
        rx={trackRx}
      />
      <circle
        ref={circleRef}
        cx={ANCHOR_R}
        cy={cy}
        r={THUMB_R}
        fill={`url(#${gradientId})`}
        strokeWidth={THUMB_STROKE_W}
        stroke={THUMB_STROKE}
        style={THUMB_STYLE}
      />
    </svg>
  );
};
